import { Flex, Skeleton, Typography, Popover, DatePicker } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getActivity } from 'shared/config/store/selectors/app';
import { RootState } from 'shared/config/store/store';
import { getEMIMonthSpend, getEMIAccounts } from 'shared/config/store/reducers/accountsSlice';
import classes from '../ui/Transactions.module.scss';
import { useLazyGetMonthSpendQuery } from '../api';
import { CalendarOutlined } from '@ant-design/icons';
import { useState, useEffect, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { EMI } from 'shared/model/types';

export const MonthSpendingAllAccounts = ({ 
  emi,
  start,
  onDateRangeChange 
}: { 
  emi: EMI,
  start: string,
  onDateRangeChange?: (startDate: string, endDate: string | undefined) => void
}) => {
  const { formatMessage: t } = useIntl();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  
  const startDate = dateRange?.[0] ? dateRange[0].format('DD-MM-YYYY') : start;
  const endDate = dateRange?.[1] ? dateRange[1].format('DD-MM-YYYY') : undefined;
  
  const accounts = useSelector((state: RootState) => getEMIAccounts(state, emi));
  const accountIds = useMemo(() => Object.keys(accounts).map(id => Number(id)), [accounts]);
  
  const [fetchMonthSpend] = useLazyGetMonthSpendQuery();
  
  useEffect(() => {
    if (accountIds.length > 0) {
      accountIds.forEach(accountId => {
        fetchMonthSpend({ accountId, start: startDate, end: endDate });
      });
    }
  }, [startDate, endDate, accountIds, fetchMonthSpend]);
  
  const monthSpend = useSelector((state: RootState) => getEMIMonthSpend(state, emi));
  const activity = useSelector(getActivity);
  
  const totalMonthSpend = useMemo(() => {
    return Object.values(monthSpend || {}).reduce((sum, spend) => sum + (spend || 0), 0);
  }, [monthSpend]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
      setPopoverOpen(false);
      const startDateStr = dates[0].format('DD-MM-YYYY');
      const endDateStr = dates[1].format('DD-MM-YYYY');
      onDateRangeChange?.(startDateStr, endDateStr);
    } else {
      setDateRange(null);
      onDateRangeChange?.(start, undefined);
    }
  };

  const getDisplayText = () => {
    if (dateRange?.[0] && dateRange?.[1]) {
      const startFormatted = dateRange[0].format('DD.MM.YYYY');
      const endFormatted = dateRange[1].format('DD.MM.YYYY');
      return t(
        { id: 'transactions.spent.period' },
        { start: startFormatted, end: endFormatted }
      );
    }
    return t(
      { id: 'transactions.spent' },
      { month: t({ id: `calendar.month.${new Date().getMonth()}` }) }
    );
  };

  const datePickerContent = (
    <DatePicker.RangePicker
      onChange={handleDateRangeChange}
      value={dateRange}
      format="DD.MM.YYYY"
      disabledDate={(current: Dayjs) => current && current.isAfter(dayjs())}
    />
  );

  return (
    <Flex justify='space-between' align='center'>
      <Flex align='center' gap={8}>
        <Popover
          content={datePickerContent}
          trigger="click"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          placement="bottomLeft"
        >
          <CalendarOutlined 
            style={{ fontSize: 18, cursor: 'pointer', color: '#000' }} 
          />
        </Popover>
        <Typography.Text className={classes.text}>
          {getDisplayText()}
        </Typography.Text>
      </Flex>
      <Skeleton active loading={activity} paragraph={{ rows: 1 }}>
        <Typography.Text className={classes.balance}>
          {(totalMonthSpend)?.toFixed(2)} €
        </Typography.Text>
      </Skeleton>
    </Flex>
  );
};

